import './includes/style.css';
import {Helmet} from 'react-helmet-async';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import {AuthProvider} from './components/AuthProvider.jsx';
import {SSOSync} from './components/SSOSync.jsx';
import {NotificationProvider} from './components/NotificationContext.jsx';
import {LoadingProvider} from './components/LoadingContext.jsx';
import {LoadingBar} from './components/LoadingBar.jsx';
import {PageContextCtx} from './hooks/usePageContext.js';
import {Header} from './components/Header.jsx';
import {EventBanner} from './components/EventBanner.jsx';
import {Footer} from './components/Footer.jsx';
import messages from './messages/messages.js';
import {useSentryErrorTracking} from './hooks/useSentryErrorTracking.js';

// Site-wide head tags: og:site_name (the PHP layer used to emit it via the
// meta API's extraMeta on every page) and the WebSite JSON-LD.
function SiteHead() {
    return (
        <Helmet>
            <meta property="og:site_name" content={messages['SITE.NAME']}/>
            <script type="application/ld+json">
                {JSON.stringify({
                    '@context': 'https://schema.org/',
                    '@type': 'WebSite',
                    url: `https://www.${messages['DOMAIN']}/`,
                    name: messages['SITE.NAME'],
                    description: `The Home of ${messages['SITE.NAME']} Music`,
                    potentialAction: {
                        '@type': 'SearchAction',
                        target: `https://www.${messages['DOMAIN']}/search?q={search_term_string}`,
                        'query-input': 'required name=search_term_string',
                    },
                })}
            </script>
        </Helmet>
    );
}

// Ports src/index.jsx's old App() layout branching: 'fm' renders bare
// (just the page, e.g. Radio), 'vanity' gets the Header only (subscriber
// sub-sites), 'main' gets the full chrome (Header/EventBanner/Footer).
function Chrome({rootMode, children}) {
    if (rootMode === 'fm') {
        return children;
    }

    if (rootMode === 'vanity') {
        return (
            <>
                <SiteHead/>
                <Header isMain={false}/>
                {children}
            </>
        );
    }

    return (
        <>
            <SiteHead/>
            <Header isMain={true}/>
            <EventBanner/>
            <div className="main-body">
                {children}
            </div>
            <Footer/>
        </>
    );
}

export function PageShell({pageContext, children}) {
    // Forwards unhandled promise rejections to Sentry. The old App() root
    // called this; it was dropped in the move to Vike. Effect-only, so it is
    // a no-op during SSR.
    useSentryErrorTracking();

    const rootMode = pageContext?.rootMode ?? 'main';

    return (
        <PageContextCtx.Provider value={pageContext}>
            <ErrorBoundary>
                <AuthProvider>
                    <SSOSync/>
                    <NotificationProvider>
                        <LoadingProvider>
                            <LoadingBar/>
                            <Chrome rootMode={rootMode}>{children}</Chrome>
                        </LoadingProvider>
                    </NotificationProvider>
                </AuthProvider>
            </ErrorBoundary>
        </PageContextCtx.Provider>
    );
}
